import { TMemberClassBadge } from '~/api/services/member-class-api/index.type'

export interface ILeisureWebApiResponse<Body> {
  traceId: string
  page?: number
  collections: boolean
  contentType: string
  body: Body
}

export const LeisureWebApiProductStatusCode = {
  // 판매 예정
  WAITING_FOR_SALE: 'WAITING_FOR_SALE',
  // 판매 중
  IN_SALE: 'IN_SALE',
  // 품절
  SOLD_OUT: 'SOLD_OUT',
  // 판매 종료
  END_OF_SALE: 'END_OF_SALE',
}
export type TLeisureWebApiProductStatusCode =
  (typeof LeisureWebApiProductStatusCode)[keyof typeof LeisureWebApiProductStatusCode]

export const LeisureWebApiPurchaseOptionTypeCode = {
  // 단일선택형 옵션
  MONO: 'MONO',
  // 조합형 옵션
  COMBINATION: 'COMBINATION',
  // 예약-날짜형 옵션
  SCHEDULE: 'SCHEDULE',
  // 예약-회차형 옵션
  ROUND: 'ROUND',
}
export type TLeisureWebApiPurchaseOptionTypeCode =
  (typeof LeisureWebApiPurchaseOptionTypeCode)[keyof typeof LeisureWebApiPurchaseOptionTypeCode]

export const LeisureWebApiProductTypeCode = {
  // 레저
  LEISURE: 'LEISURE',
  // 모바일교환권
  GIFTICON: 'GIFTICON',
  // 렌터카
  RENTCAR: 'RENTCAR',
} as const
export type TLeisureWebApiProductTypeCode =
  (typeof LeisureWebApiProductTypeCode)[keyof typeof LeisureWebApiProductTypeCode]

export interface ILeisureWebApiInfoContent {
  content?: string
  subContents?: ILeisureWebApiInfoContent[]
}

export const LeisureWebApiProductPromotionCode = {
  // 핫딜
  HOT_DEAL: 'HOT_DEAL',
  // 최저가
  LOWEST_PRICE: 'LOWEST_PRICE',
  // 인기상품
  POPULAR: 'POPULAR',
  // 쇼킹특가
  SPECIAL_PRICE: 'SPECIAL_PRICE',
}
export type TLeisureWebApiProductPromotionCode =
  (typeof LeisureWebApiProductPromotionCode)[keyof typeof LeisureWebApiProductPromotionCode]

export const LeisureWebApiProductEventCode = {
  // 선착순
  FIRST_BASIS: 'FIRST_BASIS',
  // 단독구성
  EXCLUSIVE: 'EXCLUSIVE',
  // 기간한정
  LIMITED_PERIOD: 'LIMITED_PERIOD',
  // 레포츠대전
  PLAY_BATTLE: 'PLAY_BATTLE',
  // 어촌대전
  FISHING_VILLAGE_BATTLE: 'FISHING_VILLAGE_BATTLE',
  // 킹특가
  KING_SPECIAL_PRICE: 'KING_SPECIAL_PRICE',
}
export type TLeisureWebApiProductEventCode =
  (typeof LeisureWebApiProductEventCode)[keyof typeof LeisureWebApiProductEventCode]

export interface ILeisureWebApiProduct {
  productId: number
  productName: string
  productVersion: number
  // 회원 전용 상품 여부
  isMemberOnly: boolean
  memberOnlyLabel?: string
  productStatusCode: TLeisureWebApiProductStatusCode
  productStatusCodeLabel?: string
  // 구매 옵션 유형 코드
  purchaseOptionTypeCode: TLeisureWebApiPurchaseOptionTypeCode
  productTypeCode: TLeisureWebApiProductTypeCode
  productTypeCodeLabel?: string
  // 취소 수수료 여부
  isCancelPenalty: boolean
  productImage: {
    thumbnailImageUrl?: string
    rollingImageUrls?: string[]
    detailImageUrls?: string[]
  }
  facilities: {
    facilityName: string
    isRepFacility: boolean
    address: string
    latitude: number
    longitude: number
    // 오시는길, 문의전화, 운영시간 등 부가 정보
    facilityInfo?: ILeisureWebApiInfoContent[]
  }[]
  productPromotions?: TLeisureWebApiProductPromotionCode[]
  productInfo?: {
    // 기본 정보, 사용 안내, 유의사항
    usageInfo?: ILeisureWebApiInfoContent[]
    // 취소/환불 규정
    refundInfo?: ILeisureWebApiInfoContent[]
    // 고객센터의 업체명, 문의번호, 운영시간
    serviceCenterInfo?: ILeisureWebApiInfoContent[]
  }
  braze?: {
    cityName?: string
    cityDetail?: string
    categoryId?: string
    categoryName?: string
    propertyPrice?: number
  }
  // 상품 이벤트 유형 코드
  productEvent?: {
    productEventTypeCode: TLeisureWebApiProductEventCode
    productEventTypeCodeLabel?: string
    startDateTime?: string
    endDateTime?: string
  }
  productOptionGroups: {
    productOptionGroupId: number
    productOptionGroupName: string
  }[]
  /** 대기열 사용 상품 여부*/
  isWaitingRoomUsed: boolean
  /** 재고 선점 사용 상품 여부 */
  isPreemptionInventoryBeforePurchase: boolean
  /** 장바구니 이용 가능 여부 */
  isCartAvailable: boolean
  /** 추가 구매만 가능 여부 (ex 화담숲 모노레일 추가 구매) */
  isAdditionalPurchaseOnly: boolean
  /** 추가구매 상품 정보 */
  additionalPurchaseProduct?: ILeisureWebApiAdditionalPurchaseProduct
  /** 구매시 본인인증 필수 여부 */
  isIdentityVerificationRequired: boolean
}

export interface ILeisureWebApiProductPartner {
  partnerCode: string
  companyName: string
  representativeName: string
  address: string
  email: string
  phoneNumber: string
  businessRegistrationNumber: string
  mailOrderSalesNumber?: string
}

export interface ILeisureWebApiVariantByIdParams {
  productId: number
  variantId: number
  productVersion?: number
  date?: string
  time?: string
}

export const LeisureWebApiVariantStatusCode = {
  // 판매 예정
  WAITING_FOR_SALE: 'WAITING_FOR_SALE',
  // 판매 중
  IN_SALE: 'IN_SALE',
  // 품절
  SOLD_OUT: 'SOLD_OUT',
  // 판매 종료
  END_OF_SALE: 'END_OF_SALE',
}
export type TLeisureWebApiVariantStatusCode =
  (typeof LeisureWebApiVariantStatusCode)[keyof typeof LeisureWebApiVariantStatusCode]

export const LeisureWebApiVariantRefundApprovalTypeCode = {
  // 즉시
  DIRECT: 'DIRECT',
  // 관리자승인
  ADMIN: 'ADMIN',
}
export type TLeisureWebApiVariantRefundApprovalTypeCode =
  (typeof LeisureWebApiVariantRefundApprovalTypeCode)[keyof typeof LeisureWebApiVariantRefundApprovalTypeCode]

export const LeisureWebApiVariantValidityPeriodTypeCode = {
  // 기간고정
  FIX: 'FIX',
  // 구매일기준
  BUY: 'BUY',
}
export type TLeisureWebApiVariantValidityPeriodTypeCode =
  (typeof LeisureWebApiVariantValidityPeriodTypeCode)[keyof typeof LeisureWebApiVariantValidityPeriodTypeCode]

export const LeisureWebApiVariantDiscountTypeCode = {
  // 할인 없음
  NONE: 'NONE',
  // 고정 비율
  FIXED_RATE: 'FIXED_RATE',
  // 고정 금액
  FIXED_AMOUNT: 'FIXED_AMOUNT',
}
export type TLeisureWebApiVariantDiscountTypeCode =
  (typeof LeisureWebApiVariantDiscountTypeCode)[keyof typeof LeisureWebApiVariantDiscountTypeCode]

/** 회원 등급 코드 */
export const LeisureWebApiMemberClassTypeCode = {
  /** 일반 */
  LEVEL_00: 'LEVEL_00',
  /** 골드 */
  LEVEL_01: 'LEVEL_01',
} as const
export type TLeisureWebApiMemberClassTypeCode =
  (typeof LeisureWebApiMemberClassTypeCode)[keyof typeof LeisureWebApiMemberClassTypeCode]

/** 취소유형코드 */
export const LeisureWebApiVariantCancellationTypeCode = {
  /** 무료취소 (무료취소가능한 기간을 의미, fee == 0) */
  FREE: 'FREE',
  /** 요율별 취소수수료 부과 (0 < fee < 100) */
  FEE: 'FEE',
  /** 취소 및 환불불가 (fee = 100) */
  NO_REFUND: 'NO_REFUND',
  /** 취소 수수료 있음 (수기 정책) fee == 0 */
  MANUAL_FEE: 'MANUAL_FEE',
  /** 미사용 100%환불의 의미 */
  FULL_REFUND: 'FULL_REFUND',
  /** 취소수수료 정책을 따름 (미이용 자동환불) */
  FEE_AUTO_REFUND: 'FEE_AUTO_REFUND',
}
export type TLeisureWebApiVariantCancellationTypeCode =
  (typeof LeisureWebApiVariantCancellationTypeCode)[keyof typeof LeisureWebApiVariantCancellationTypeCode]

/**
 * 취소수수료 정책
 *
 * @see {@link https://leisure-api-doc.dev.yanolja.in/?api=leisure-web-api#tag/Product/operation/getVariant}
 */
export interface ILeisureWebApiCancellationFeePlan {
  /** KST 기준 시작일시 */
  startDateTime: string
  /** KST 기준 종료일시 */
  endDateTime: string
  /** 취소유형코드 */
  cancellationTypeCode: TLeisureWebApiVariantCancellationTypeCode
  /** 취소수수료 유형 코드 */
  cancellationFeeTypeCode: string
  /** 취소수수료 유형코드에 따라서 비율로 30인경우 30%라고 인지 */
  fee: number
}

export interface ILeisureWebApiVariant {
  variantId: number
  variantName: string
  variantDescription?: ILeisureWebApiInfoContent[]
  // 판매 시작일
  saleStartDate: string
  // 판매 종료일
  saleEndDate: string
  saleStartTime?: string
  saleEndTime?: string
  // 당일 사용 가능 여부
  isAvailableOnPurchaseDate: boolean
  // 만료 후 환불 가능 여부
  isRefundableAfterExpiration: boolean
  // 1인당 구매 제한 일 동안 구매 가능한 수량 (0 또는 -1 : 제한 없음)
  quantityPerPerson: number
  // 1인당 구매 가능 수량이 적용되는 일 수 (-1 : 제한 없음)
  quantityPerPersonValidityDays: number
  // 한번에 구매 가능한 수량 (0 또는 -1 : 제한 없음)
  quantityPerPurchase: number
  variantStatusCode: TLeisureWebApiVariantStatusCode
  variantStatusCodeLabel?: string
  // 유효기간 종료 n 일 전까지 100% 환불
  refundAvailableDays?: number
  // 환불 승인 유형 코드
  refundApprovalTypeCode?: TLeisureWebApiVariantRefundApprovalTypeCode
  // 유효기간 유형 코드
  validityPeriodTypeCode?: TLeisureWebApiVariantValidityPeriodTypeCode
  // 유효기간 시작일 (구매일기준 유형일때는 오늘)
  validityStartDateTime?: string
  // 유효기간 종료일 (구매일기준 유형일때는 오늘 + 유효기간 일 수)
  validityEndDateTime?: string
  // 유효기간 유형 코드가 구매일기준일때, 구매일로 부터 n일
  validityDays?: number
  // 가격 정보
  variantPrice: {
    salePrice: number
    discountTypeCode: TLeisureWebApiVariantDiscountTypeCode
    retailPrice?: number
    discountPercentage?: number
    discountAmount?: number
  }
  variantSupplyItems: {
    supplyItemId: number
    supplyItemName?: string
    partnerCode?: string
  }[]
  productOptionGroup: {
    productOptionGroupId: number
    productOptionGroupName?: string
  }
  // 바우처(모바일 입장권) 확인 페이지 제공여부
  isVoucherPageProvided: boolean
  // 취소수수료 정책
  cancellationFeePlans: ILeisureWebApiCancellationFeePlan[]
  // 상품명
  productName: string
  /** 구매 가능한 회원등급 코드 (미전달 시 LEVEL_00) */
  memberClassTypeCode?: TLeisureWebApiMemberClassTypeCode
  /** 회원등급 배지 유형 코드 (회원혜택 조회 API의 badge 값)  */
  memberClassBadgeTypeCode: TMemberClassBadge
}

export interface ILeisureWebAPIVerifyTokenRequest {
  productId: number
  waitingToken?: string
  preemptionInventoryToken?: string
  variants?: {
    variantId: number
    quantity: number
    date?: string
    time?: string
  }[]
}

export interface ILeisureWebApiVerifyToken {
  expirationDateTime: string
  currentDateTime: string
}

export interface ILeisureWebApiAdditionalPurchaseProduct {
  /** 추가구매 상품 ID */
  productId: string
  /** 추가구매 상품명 */
  productName: string
}

export const LeisureWebApiVerifyWaitingTokenErrorCode = {
  // 대기열 토큰이 유효하지 않습니다.
  LEISURE_WEB_API_3005: 'leisure-web-api-3005',
  // 대기열 토큰이 만료되었습니다.
  LEISURE_WEB_API_3006: 'leisure-web-api-3006',
  // 대기열 토큰검증 실패
  LEISURE_WEB_API_3007: 'leisure-web-api-3007',
  // 대기열 토큰이 존재하지 않습니다.
  LEISURE_WEB_API_3008: 'leisure-web-api-3008',
} as const

export const LeisureWebApiVerifyInventoryTokenErrorCode = {
  // 재고선점 로그인 정보가 없습니다.
  LEISURE_WEB_API_3002: 'leisure-web-api-3002',
  // 재고선점 토큰이 유효하지 않습니다.
  LEISURE_WEB_API_3009: 'leisure-web-api-3009',
  // 재고선점 토큰이 만료되었습니다.
  LEISURE_WEB_API_3010: 'leisure-web-api-3010',
  // 재고선점 토큰검증 실패
  LEISURE_WEB_API_3011: 'leisure-web-api-3011',
  // 재고선점 토큰이 존재하지 않습니다.
  LEISURE_WEB_API_3012: 'leisure-web-api-3012',
  // 재고선점 베리언트 정보가 존재하지 않습니다.
  LEISURE_WEB_API_3013: 'leisure-web-api-3013',
} as const

export const LeisureWebApiVerifyCookieName = {
  WAITING_TOKEN: 'waiting_token',
  PREEMPTION_INVENTORY_TOKEN: 'preemption_inventory_token',
  WAITING_TOKEN_REQUEST_ID: 'wrid',
} as const
